import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { gameApi } from '../lib/api/game-service';
import { useGlobalWebSocket } from './GlobalWebSocketContext';
import { useAuthState } from './AuthContext';

export interface GameMove {
  id: string;
  round: number;
  player: string;
  type: string;
  prompt?: string;
  response?: string;
  choice?: string;
  points?: number;
  completed: boolean;
}

export interface Game {
  id: string;
  game_type: string;
  status: string;
  character_id: number;
  entry_cost: number;
  coins_per_round: number;
  max_rounds: number;
  current_round: number;
  user_score: number;
  character_score: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  expires_at?: string;
}

export interface GameState {
  game: Game;
  moves: GameMove[];
}

export interface AvailableGame {
  id: string;
  type: string;
  name: string;
  description: string;
  difficulty: number;
  category: string;
  entry_cost: number;
  coins_per_round: number;
  default_rounds: number;
  can_afford: boolean;
  plays_count: number;
  total_plays: number;
  average_rating: number;
  requires_subscription: boolean;
}

export interface GameStatistics {
  total_games: number;
  games_by_status: Record<string, number>;
  games_by_type: Record<string, number>;
  total_coins_spent: number;
  average_rating_given: number;
  favorite_game_type?: string;
  completed_games: number;
  active_games: number;
}

interface GameContextType {
  // Available games
  availableGames: AvailableGame[];
  loadAvailableGames: () => Promise<void>;
  
  // Current game state
  currentGame: GameState | null;
  isGameLoading: boolean;
  gameError: string | null;
  
  // Game management
  createGame: (characterId: number, gameType: string, options?: Record<string, any>) => Promise<Game>;
  startGame: (gameId: string) => Promise<Game>;
  joinGame: (gameId: string) => Promise<void>;
  leaveGame: () => Promise<void>;
  
  // Game actions
  makeGameAction: (action: string, data?: Record<string, any>) => Promise<any>;
  
  // Game history
  gameHistory: Game[];
  loadGameHistory: (status?: string, limit?: number) => Promise<void>;
  
  // Statistics
  gameStats: GameStatistics | null;
  loadGameStatistics: () => Promise<void>;
  
  // Rating
  rateGame: (gameId: string, rating: number, feedback?: string, categories?: Record<string, number>) => Promise<void>;
  
  // Real-time updates
  isUserTurn: boolean;
  turnData: Record<string, any>;
  lastAction: any;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [availableGames, setAvailableGames] = useState<AvailableGame[]>([]);
  const [currentGame, setCurrentGame] = useState<GameState | null>(null);
  const [isGameLoading, setIsGameLoading] = useState(false);
  const [gameError, setGameError] = useState<string | null>(null);
  const [gameHistory, setGameHistory] = useState<Game[]>([]);
  const [gameStats, setGameStats] = useState<GameStatistics | null>(null);
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [turnData, setTurnData] = useState<Record<string, any>>({});
  const [lastAction, setLastAction] = useState<any>(null);
  
  const { subscribe, sendMessage } = useGlobalWebSocket();
  const { user } = useAuthState();
  
  // Use ref to access current game in callbacks without causing re-renders
  const currentGameRef = React.useRef<GameState | null>(null);
  React.useEffect(() => {
    currentGameRef.current = currentGame;
  }, [currentGame]);
  
  useEffect(() => {
    const unsubscribe = subscribe((data) => {
      // Handle WebSocket game events
      switch (data.type) {
        case 'game_update':
          console.log('Game update received:', data);
          if (data.data.game_state) {
            setCurrentGame(data.data.game_state);
          }
          if (data.data.last_action) {
            setLastAction(data.data.action_result);
          }
          break;

        case 'game_action_result':
          console.log('Game action result:', data);
          setLastAction(data.result);
          break;

        case 'game_turn':
          console.log('Game turn update:', data);
          setIsUserTurn(data.is_your_turn);
          setTurnData(data.turn_data || {});
          break;

        case 'game_joined':
          console.log('Joined game:', data);
          setCurrentGame(data.game_state);
          setGameError(null);
          break;

        case 'game_left':
          console.log('Left game:', data);
          setCurrentGame(null);
          setIsUserTurn(false);
          setTurnData({});
          break;

        case 'game_error':
          console.error('Game error:', data);
          setGameError(data.error);
          setIsGameLoading(false);
          break;
      }
    });
    
    return unsubscribe;
  }, [subscribe]);

  const loadAvailableGames = useCallback(async () => {
    try {
      const response = await gameApi.getAvailableGames();
      setAvailableGames(response.games);
    } catch (error) {
      console.error('Failed to load available games:', error);
      setGameError('Failed to load available games');
    }
  }, []);

  const createGame = useCallback(async (
    characterId: number, 
    gameType: string, 
    options: Record<string, any> = {}
  ): Promise<Game> => {
    setIsGameLoading(true);
    setGameError(null);
    
    try {
      const game = await gameApi.createGame({
        character_id: characterId,
        game_type: gameType,
        options
      });
      
      return game;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to create game';
      setGameError(errorMessage);
      throw error;
    } finally {
      setIsGameLoading(false);
    }
  }, []);

  const startGame = useCallback(async (gameId: string): Promise<Game> => {
    setIsGameLoading(true);
    setGameError(null);
    
    try {
      const game = await gameApi.startGame(gameId);
      return game;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to start game';
      setGameError(errorMessage);
      throw error;
    } finally {
      setIsGameLoading(false);
    }
  }, []);

  const joinGame = useCallback(async (gameId: string): Promise<void> => {
    setIsGameLoading(true);
    setGameError(null);
    
    try {
      // Send join game message via WebSocket
      sendMessage({ type: 'join_game', game_id: gameId });
      
      // Also get initial game state via API
      const gameState = await gameApi.getGameState(gameId);
      setCurrentGame(gameState);
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to join game';
      setGameError(errorMessage);
      throw error;
    } finally {
      setIsGameLoading(false);
    }
  }, [sendMessage]);

  const leaveGame = useCallback(async (): Promise<void> => {
    // Get current game from ref to avoid dependency
    const game = currentGameRef.current;
    if (!game) return;
    
    try {
      sendMessage({ type: 'leave_game', game_id: game.game.id });
      setCurrentGame(null);
      setIsUserTurn(false);
      setTurnData({});
    } catch (error) {
      console.error('Failed to leave game:', error);
    }
  }, [sendMessage]); // Remove currentGame from dependencies

  const makeGameAction = useCallback(async (
    action: string, 
    data: Record<string, any> = {}
  ): Promise<any> => {
    const game = currentGameRef.current;
    if (!game) {
      throw new Error('No active game');
    }
    
    setGameError(null);
    
    try {
      // Send action via WebSocket for real-time updates
      sendMessage({
        type: 'game_action',
        game_id: game.game.id,
        action,
        data
      });
      
      // Also send via API for reliability
      const result = await gameApi.processGameAction(game.game.id, {
        action,
        data
      });
      
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to process action';
      setGameError(errorMessage);
      throw error;
    }
  }, [sendMessage]);

  const loadGameHistory = useCallback(async (status?: string, limit = 20) => {
    try {
      const response = await gameApi.getUserGameHistory(status, limit);
      setGameHistory(response.games);
    } catch (error) {
      console.error('Failed to load game history:', error);
    }
  }, []);

  const loadGameStatistics = useCallback(async () => {
    try {
      const stats = await gameApi.getUserStatistics();
      setGameStats(stats);
    } catch (error) {
      console.error('Failed to load game statistics:', error);
    }
  }, []);

  const rateGame = useCallback(async (
    gameId: string, 
    rating: number, 
    feedback?: string, 
    categories?: Record<string, number>
  ) => {
    try {
      await gameApi.rateGame(gameId, {
        rating,
        feedback,
        categories
      });
      
      // Refresh game history to show updated rating
      await loadGameHistory();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to rate game';
      setGameError(errorMessage);
      throw error;
    }
  }, [loadGameHistory]);

  // Load available games on mount
  useEffect(() => {
    if (user) {
      void loadAvailableGames();
    }
  }, [user, loadAvailableGames]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<GameContextType>(() => ({
    availableGames,
    loadAvailableGames,
    currentGame,
    isGameLoading,
    gameError,
    createGame,
    startGame,
    joinGame,
    leaveGame,
    makeGameAction,
    gameHistory,
    loadGameHistory,
    gameStats,
    loadGameStatistics,
    rateGame,
    isUserTurn,
    turnData,
    lastAction
  }), [
    availableGames,
    loadAvailableGames,
    currentGame,
    isGameLoading,
    gameError,
    createGame,
    startGame,
    joinGame,
    leaveGame,
    makeGameAction,
    gameHistory,
    loadGameHistory,
    gameStats,
    loadGameStatistics,
    rateGame,
    isUserTurn,
    turnData,
    lastAction
  ]);

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = (): GameContextType => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};